import React, { useState, useEffect } from 'react';
import Icon from '../../components/Icon';

const PLACEHOLDER_EARLY = [
  { season: 2015, note: 'Early era · Data pending' },
  { season: 2014, note: 'Early era · Data pending' },
  { season: 2013, note: 'Early era · Data pending' },
  { season: 2012, note: 'Founding season · Data pending' },
];

function SourceBadge({ source }) {
  if (source === 'sleeper') return (
    <span className="lor-history-source lor-history-source--sleeper">🔗 Sleeper</span>
  );
  if (source === 'manual') return (
    <span className="lor-history-source lor-history-source--manual">✏️ Manual</span>
  );
  return null;
}

function StandingsTable({ standings }) {
  if (!standings || standings.length === 0) return null;
  return (
    <div className="lor-history-standings">
      <div className="lor-history-section-label">Final Standings</div>
      <table className="lor-standings-table lor-standings-table--compact">
        <thead>
          <tr>
            <th>RK</th>
            <th>Manager</th>
            <th>W</th>
            <th>L</th>
            <th>PF</th>
            <th>PA</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((s, i) => (
            <tr key={i} className={s.rank <= 4 ? 'lor-playoff-spot' : ''}>
              <td className="lor-rank">{s.rank || i + 1}</td>
              <td>
                <span className="lor-manager-name">{s.display_name}</span>
              </td>
              <td className="lor-wins">{s.wins}</td>
              <td className="lor-losses">{s.losses}</td>
              <td className="lor-pf">{typeof s.pf === 'number' ? s.pf.toFixed(2) : s.pf}</td>
              <td style={{ color: 'var(--text3)' }}>{typeof s.pa === 'number' ? s.pa.toFixed(2) : s.pa}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PlayoffResults({ matchups }) {
  if (!matchups || matchups.length === 0) return null;
  const weeks = [...new Set(matchups.map(m => m.week))].sort((a, b) => a - b);
  return (
    <div className="lor-history-playoffs">
      <div className="lor-history-section-label">Playoff Results</div>
      {weeks.map(week => {
        const games = matchups.filter(m => m.week === week);
        const label = week === Math.max(...weeks) ? 'Championship' :
                      week === Math.max(...weeks) - 1 ? 'Semifinals' : `Week ${week}`;
        return (
          <div key={week} className="lor-history-playoff-round">
            <div className="lor-history-playoff-round-label">{label}</div>
            {games.map((g, i) => (
              <div key={i} className="lor-history-playoff-game">
                <span className="lor-history-playoff-winner"><Icon emoji="🏆" size={13} style={{ verticalAlign: 'middle', marginRight: 3 }} /> {g.winner}</span>
                <span className="lor-history-playoff-score">{g.score}</span>
                <span className="lor-history-playoff-loser">{g.loser}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function SeasonCard({ season, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen || false);
  const yr       = season.season;
  const source   = season.source;
  const champion = season.champion_name;
  const runner   = season.runner_up_name;
  const narrative = season.narrative;
  const rsChamp  = season.regular_season_champion;
  const highScore = season.season_high_score;
  const highScorer = season.season_high_scorer;
  const standings  = season.standings || [];
  const playoffs   = season.playoff_matchups || [];

  const isPending = source === 'pending';

  return (
    <div className={`lor-history-season-card${isPending ? ' lor-history-season-card--pending' : ''}`}>
      <button
        className="lor-history-season-header"
        onClick={() => !isPending && setOpen(o => !o)}
        disabled={isPending}
      >
        <div className="lor-history-season-year">
          <span className="lor-history-year-num">{yr}</span>
          <SourceBadge source={source} />
        </div>
        <div className="lor-history-season-summary">
          {champion ? (
            <>
              <span className="lor-trophy"><Icon emoji="🏆" size={14} style={{ verticalAlign: 'middle' }} /></span>
              <strong className="lor-history-champ-name">{champion}</strong>
              {runner && (
                <span className="lor-history-runner"> vs {runner}</span>
              )}
            </>
          ) : (
            <span className="lor-history-pending-inline">Data pending</span>
          )}
        </div>
        {!isPending && (
          <span className="lor-history-chevron">{open ? '▲' : '▼'}</span>
        )}
      </button>

      {open && !isPending && (
        <div className="lor-history-season-body">
          {narrative && (
            <div className="lor-history-narrative">"{narrative}"</div>
          )}

          <div className="lor-history-stats-row">
            {rsChamp && rsChamp !== champion && (
              <div className="lor-history-stat">
                <div className="lor-history-stat-label">Reg. Season Champion</div>
                <div className="lor-history-stat-val">{rsChamp}</div>
              </div>
            )}
            {highScore > 0 && (
              <div className="lor-history-stat">
                <div className="lor-history-stat-label">Season High</div>
                <div className="lor-history-stat-val lor-pf">{highScore.toFixed(2)}</div>
                {highScorer && <div className="lor-history-stat-sub">{highScorer}</div>}
              </div>
            )}
          </div>

          <PlayoffResults matchups={playoffs} />
          <StandingsTable standings={standings} />
        </div>
      )}
    </div>
  );
}

export default function LORHistory({ api }) {
  const [seasons, setSeasons] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [activeYear, setActiveYear] = useState(null);

  useEffect(() => {
    fetch(`${api}/api/lor/history/full`)
      .then(r => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json();
      })
      .then(data => {
        setSeasons(data || []);
        setLoading(false);
        if (data && data.length > 0) setActiveYear(data[0].season);
      })
      .catch(() => {
        // Fall back to basic history endpoint
        fetch(`${api}/api/lor/history`)
          .then(r => r.json())
          .then(data => {
            const auto   = data.auto_seasons || [];
            const manual = data.manual_seasons || [];
            const byYear = {};
            for (const s of auto)   byYear[s.season] = { ...s, source: 'sleeper' };
            for (const s of manual) byYear[s.season] = { ...s, source: 'manual' };
            for (let y = 2016; y <= 2021; y++) {
              if (!byYear[y]) byYear[y] = { season: y, source: 'pending', champion_name: '', runner_up_name: '', standings: [], playoff_matchups: [] };
            }
            const sorted = Object.values(byYear).sort((a, b) => b.season - a.season);
            setSeasons(sorted);
            setLoading(false);
            if (sorted.length > 0) setActiveYear(sorted[0].season);
          })
          .catch(() => { setError('Failed to load history archive.'); setLoading(false); });
      });
  }, [api]);

  if (loading) return <div className="lor-loading">Loading history archive...</div>;
  if (error)   return <div className="lor-empty">{error}</div>;

  const mainSeasons = seasons || [];
  const yearsInTimeline = new Set(mainSeasons.map(s => s.season));

  // Only show placeholder rows for founding-era years not already in mainSeasons
  const foundingPlaceholders = PLACEHOLDER_EARLY.filter(p => !yearsInTimeline.has(p.season));
  const showFoundingEra = foundingPlaceholders.length > 0;

  // Year tabs: main timeline years only (founding era years that have data are already included)
  const years = mainSeasons.map(s => s.season).sort((a, b) => b - a);
  const totalSeasons = mainSeasons.length + foundingPlaceholders.length;

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title">📜 History Archive</h2>
        <div className="lor-page-meta">
          League of Record · Est. 2012 · {totalSeasons} seasons
        </div>
      </div>

      {/* Year tabs */}
      <div className="lor-history-year-tabs">
        {years.map(yr => (
          <button
            key={yr}
            className={`lor-history-year-tab${activeYear === yr ? ' active' : ''}`}
            onClick={() => {
              setActiveYear(yr);
              document.getElementById(`lor-season-${yr}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          >
            {yr}
          </button>
        ))}
        {showFoundingEra && (
          <button
            className="lor-history-year-tab lor-history-year-tab--era"
            onClick={() => document.getElementById('lor-founding-era')?.scrollIntoView({ behavior: 'smooth' })}
          >
            2012–15
          </button>
        )}
      </div>

      <div className="lor-history-timeline">
        {mainSeasons.map(season => (
          <div key={season.season} id={`lor-season-${season.season}`}>
            <SeasonCard
              season={season}
              defaultOpen={season.season === activeYear}
            />
          </div>
        ))}

        {/* 2012-2015 founding era — only for years with no data */}
        {showFoundingEra && (
          <>
            <div id="lor-founding-era" className="lor-history-era-header">
              <span>📁 Founding Era (2012–2015)</span>
              <span className="lor-history-era-note">Data to be added by commissioner</span>
            </div>
            {foundingPlaceholders.map(({ season, note }) => (
              <div key={season} className="lor-history-season-card lor-history-season-card--pending">
                <div className="lor-history-season-header" style={{ cursor: 'default' }}>
                  <div className="lor-history-season-year">
                    <span className="lor-history-year-num">{season}</span>
                  </div>
                  <div className="lor-history-season-summary">
                    <span className="lor-history-pending-inline">{note}</span>
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="lor-history-note">
        <strong>2022–present</strong> pulled automatically from Sleeper. ·{' '}
        <strong>2016–2021</strong> entered by the commissioner. ·{' '}
        <strong>2012–2015</strong> founding era data to be added.
      </div>
    </div>
  );
}
